import React, { useEffect, useState } from 'react';

import { Col } from '@opendoor/atoms/box';
import { cn } from '@opendoor/atoms/utils';

/** @public */
export type FlagState = {
  enabled: boolean;
  loading: boolean;
};

export type FlagEvaluator = (flag: string) => FlagState;
export type AsyncFlagEvaluator = (flag: string) => Promise<boolean>;

type ButtonSize = 'xsmall' | 'small' | 'medium' | 'large';

const SKELETON_SHAPE: Record<ButtonSize, string> = {
  xsmall: 'h-8 rounded-xl',
  small: 'h-8 rounded-xl',
  medium: 'h-11 rounded-2xl',
  large: 'h-14 rounded-2xl',
};

/**
 * When set, the gate shows a button-sized shimmer skeleton while the flag
 * resolves, then fade-slides the `enabled` node in. `buttonSize`/`fullWidth`
 * match the gated redirect button's footprint so nothing shifts on resolve.
 *
 * @public
 */
export interface RevealConfig {
  buttonSize?: ButtonSize;
  fullWidth?: boolean;
  /**
   * Applied to both the skeleton and the revealed wrapper so surrounding
   * spacing (e.g. the gap to the button above) is identical in both states
   * and collapses entirely when the flag is off.
   */
  className?: string;
}

const revealWidth = (fullWidth?: boolean) => (fullWidth ? 'w-full' : 'w-full max-w-[25rem]');

const RevealSkeleton: React.FC<RevealConfig> = ({ buttonSize = 'large', fullWidth, className }) => (
  <Col className={cn(revealWidth(fullWidth), className)}>
    <div
      aria-hidden
      className={cn('reveal-skeleton w-full', SKELETON_SHAPE[buttonSize])}
      data-testid="flag-gate-skeleton"
    />
  </Col>
);

export interface FeatureFlagGateProps {
  /** Feature flag key to evaluate */
  flag: string;
  /** Consumer-provided evaluator that resolves flag state synchronously */
  evaluate: FlagEvaluator;
  /** Rendered when flag is enabled */
  enabled: React.ReactNode;
  /** Rendered when flag is disabled */
  disabled: React.ReactNode;
  /**
   * Rendered while the flag is loading. Falls back to `disabled` when not
   * passed. When `disabled` is an interactive tree (a form, etc.), pass an
   * explicit loader/skeleton so the user can't start typing into the
   * `disabled` tree only to lose that in-flight state when the flag
   * resolves `enabled` and React swaps trees.
   */
  loading?: React.ReactNode;
  /**
   * Opt-in reveal treatment: skeleton while loading + fade-slide on enable.
   * Overrides `loading` with the sized skeleton and wraps `enabled`.
   */
  reveal?: RevealConfig;
  /**
   * Renders the `disabled` arm without reading the flag.
   *
   * This selects an arm, not an implementation. A caller that set it to pin a
   * particular component is relying on what that arm happened to hold, and gets
   * whatever it holds now — silently, since the arms are typed the same.
   */
  forceDisabled?: boolean;
}

export interface AsyncFeatureFlagGateProps extends Omit<FeatureFlagGateProps, 'evaluate'> {
  /**
   * Consumer-provided evaluator that resolves to whether the flag is enabled.
   * The library owns loading state — the promise pending is the loading signal.
   * Memoize with `useCallback` to avoid re-firing the resolver on every render.
   */
  evaluate: AsyncFlagEvaluator;
}

export type DefaultAsyncFeatureFlagProps = Omit<
  AsyncFeatureFlagGateProps,
  'flag' | 'enabled' | 'disabled'
> &
  Partial<Pick<AsyncFeatureFlagGateProps, 'flag'>>;

const revealWrap = (node: React.ReactNode, { fullWidth, className }: RevealConfig) => (
  <Col className={cn('reveal-fade-slide-in', revealWidth(fullWidth), className)}>{node}</Col>
);

export const FeatureFlagGate: React.FC<FeatureFlagGateProps> = ({
  flag,
  evaluate,
  enabled: enabledComponent,
  disabled: disabledComponent,
  loading: loadingComponent,
  reveal,
  forceDisabled = false,
}) => {
  if (forceDisabled) {
    return disabledComponent;
  }

  const { enabled, loading } = evaluate(flag);

  if (loading) {
    return reveal ? <RevealSkeleton {...reveal} /> : (loadingComponent ?? disabledComponent);
  }

  if (!enabled) {
    return disabledComponent;
  }

  return reveal ? revealWrap(enabledComponent, reveal) : enabledComponent;
};

const useAsyncFlag = (flag: string, evaluate: AsyncFlagEvaluator, skip: boolean): FlagState => {
  const [state, setState] = useState<FlagState>({
    enabled: false,
    loading: !skip,
  });

  useEffect(() => {
    if (skip) {
      setState({ enabled: false, loading: false });
      return;
    }

    let stale = false;
    setState({ enabled: false, loading: true });

    void (async () => {
      try {
        const enabled = await evaluate(flag);
        if (stale) return;
        setState({ enabled, loading: false });
      } catch (error) {
        if (stale) return;
        setState({ enabled: false, loading: false });
        console.error('AsyncFeatureFlagGate: evaluator threw for flag', flag, error);
      }
    })();

    return () => {
      stale = true;
    };
  }, [evaluate, flag, skip]);

  return state;
};

export const AsyncFeatureFlagGate: React.FC<AsyncFeatureFlagGateProps> = ({
  flag,
  evaluate,
  enabled: enabledComponent,
  disabled: disabledComponent,
  loading: loadingComponent,
  reveal,
  forceDisabled = false,
}) => {
  const { enabled, loading } = useAsyncFlag(flag, evaluate, forceDisabled);

  if (forceDisabled) {
    return disabledComponent;
  }

  if (loading) {
    return reveal ? <RevealSkeleton {...reveal} /> : (loadingComponent ?? disabledComponent);
  }

  if (!enabled) {
    return disabledComponent;
  }

  return reveal ? revealWrap(enabledComponent, reveal) : enabledComponent;
};
